export const initOnEvents = ['click', 'touchstart', 'keypress', 'keydown'];

// Live alert volume as a gain value in the 0..1 range. Stored at module scope so
// changes from the settings UI take effect immediately, without rebuilding
// `window.playAudioAlert` (which is created only once on first user interaction).
let currentGain = 1;

// Clamps a user facing 0..100 percentage and returns the matching 0..1 gain.
const percentToGain = percent => {
  const value = Number(percent);
  const safe = Number.isFinite(value) ? value : 100;
  return Math.min(Math.max(safe, 0), 100) / 100;
};

export const setAudioAlertVolume = percent => {
  currentGain = percentToGain(percent);
};

export const getAudioContext = () => {
  let audioCtx;
  try {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  } catch {
    // AudioContext is not available.
  }
  return audioCtx;
};

// Connects a buffer source to the destination through a GainNode so every alert
// respects the user's configured volume at the moment it is played.
const playBufferWithVolume = (audioCtx, audioBuffer, gain) => {
  const source = audioCtx.createBufferSource();
  source.buffer = audioBuffer;
  const gainNode = audioCtx.createGain();
  gainNode.gain.value = gain;
  source.connect(gainNode);
  gainNode.connect(audioCtx.destination);
  source.loop = false;
  source.start();
};

// eslint-disable-next-line default-param-last
export const getAlertAudio = async (baseUrl = '', requestContext) => {
  const audioCtx = getAudioContext();
  const playSound = audioBuffer => {
    window.playAudioAlert = () => {
      if (audioCtx) {
        // Read the live gain on every play so volume changes apply instantly.
        playBufferWithVolume(audioCtx, audioBuffer, currentGain);
      }
    };
  };

  if (audioCtx) {
    const { type = 'dashboard', alertTone = 'ding' } = requestContext || {};
    const resourceUrl = `${baseUrl}/audio/${type}/${alertTone}.mp3`;
    const audioRequest = new Request(resourceUrl);

    fetch(audioRequest)
      .then(response => response.arrayBuffer())
      .then(buffer => {
        audioCtx.decodeAudioData(buffer).then(playSound);
        // eslint-disable-next-line no-promise-executor-return
        return new Promise(res => res());
      })
      .catch(() => {
        // error
      });
  }
};

// Plays a one-off preview of the given tone at the given volume. Self contained
// so the settings UI can let the user hear the exact tone + volume combination
// without touching `window.playAudioAlert`.
export const playAudioAlertPreview = async ({
  baseUrl = '',
  type = 'dashboard',
  alertTone = 'ding',
  volume = 100,
} = {}) => {
  const audioCtx = getAudioContext();
  if (!audioCtx) {
    return;
  }
  try {
    const resourceUrl = `${baseUrl}/audio/${type}/${alertTone}.mp3`;
    const response = await fetch(new Request(resourceUrl));
    const buffer = await response.arrayBuffer();
    const audioBuffer = await audioCtx.decodeAudioData(buffer);
    playBufferWithVolume(audioCtx, audioBuffer, percentToGain(volume));
  } catch {
    // Ignore preview errors.
  }
};
